import './adapters/polyfill'
import * as googleAnalytics from './adapters/googleAnalytics'
import * as webExperiment from './adapters/webExperiment'
import * as segment from './adapters/segment'
import { analyticTools } from './common/constants'
import standardTracking from './common/standardTracking'
import * as standardSchemas from './common/standardSchemas'
import * as abTesting from './common/abTesting'
import {
  captureGoogleClickId,
  readGoogleClickId,
} from './common/google_click_id'
import { hashEmail } from './common/hashEmail'
import { hasAdvertisingConsent, ADVERTISING_GROUP } from './common/consent'
import {
  setAnalyticsHashedEmail,
  getAnalyticsHashedEmail,
} from './common/session'
import { logTracking } from './adapters/helpers'
import {
  initializeAmplitude,
  createAnalyticsContext,
  identifyUser,
} from './amplitude/index'
import type { PageAnalyticsContext, InitializeOptions } from './amplitude/index'
import { createFlowEmitter, composeEmitters } from './common/flowTracking'
import {
  defaultSource,
  eventNames,
  track,
  trackDataLayerLoaded,
  trackBuyingPreferencesNudge,
  trackPropertySave,
  trackSearchFilterChanged,
  trackInstrumentPageLoad,
  trackUserDomainEvent,
} from './common/eventTracking'

/* Remember current analytics enabled settings */
const analyticsEnabled = new Set<string>()

interface AnalyticsConfig {
  analytics?: {
    adc?: {
      gtm?: {
        enabled?: boolean
        default_gtm_key?: string
        gtm_id_mkt?: string
        [key: string]: any
      }
      amplitude?: {
        enabled?: boolean
        api_key?: string
        web_experiment?: {
          enabled?: boolean
          // Optional override; defaults to amplitude.api_key.
          api_key?: string
        }
      }
      [key: string]: any
    }
  }
  [key: string]: any
}

interface InitializationResult {
  initialized: boolean
  msg: string
}

interface InitializationResults {
  gtm: InitializationResult
  amp: InitializationResult
  webExp: InitializationResult
  msg: string
}

// Amplitude Web Experiment (no-code / visual) loader. Gated solely by SSP
// config so ops can toggle it per environment without a code change. Reuses
// the Amplitude project api_key unless a web_experiment-specific key is set.
const initializeWebExperiment = (
  config: AnalyticsConfig
): InitializationResult => {
  const result = {
    initialized: false,
    msg: '',
  }

  const amplitudeConfig = config.analytics?.adc?.amplitude
  const webExperimentConfig = amplitudeConfig?.web_experiment

  if (!webExperimentConfig?.enabled) {
    logTracking('web-experiment', 'Disabled')
    result.msg += 'Web Experiment disabled'
    return result
  }

  const apiKey = webExperimentConfig.api_key || amplitudeConfig?.api_key

  if (!apiKey) {
    logTracking('web-experiment', 'Missing api_key')
    result.msg += 'Web Experiment missing api_key.'
    return result
  }

  try {
    webExperiment.setUpWebExperimentScript(apiKey)
    result.initialized = true
    logTracking('web-experiment', 'Loaded')
  } catch (err) {
    result.msg += 'Error initializing Web Experiment.'
    // eslint-disable-next-line no-console
    console.error(err)
  }

  return result
}

const initializeGTM = (config: AnalyticsConfig): InitializationResult => {
  const result = {
    initialized: false,
    msg: '',
  }
  // Setup GTM
  if (config.analytics?.adc?.gtm) {
    const { adc: analyticsConfig } = config.analytics
    const toolsLoaded = []

    if (analyticsConfig[analyticTools.gtm]?.enabled) {
      const targetGTMKey =
        analyticsConfig[analyticTools.gtm]?.default_gtm_key || 'gtm_id_mkt'
      googleAnalytics.setUpGoogleTagManagerScripts(
        new Date().getTime(),
        analyticsConfig[analyticTools.gtm]?.[targetGTMKey]
      )
      toolsLoaded.push('Google Tag Manager')
      analyticsEnabled.add(analyticTools.gtm)
    }

    logTracking('gtm', `${toolsLoaded.join(', ')}`)

    result.initialized = true
  } else {
    result.msg += 'Invalid gtm config passed.'
  }
  return result
}

// Public initialization function
const makeInitFunction = () => {
  /* initialization state closure */
  let initializationPromise: Promise<InitializationResults> | null = null
  const initializationResult: InitializationResults = {
    gtm: {
      initialized: false,
      msg: '',
    },
    amp: {
      initialized: false,
      msg: '',
    },
    webExp: {
      initialized: false,
      msg: '',
    },
    msg: '',
  }

  // Internal initialization function
  const initializeAllTracking = async (
    config: AnalyticsConfig,
    ctx?: PageAnalyticsContext,
    options?: InitializeOptions
  ): Promise<void> => {
    if (!config) {
      initializationResult.msg = 'config missing'
      return Promise.resolve()
    }

    // Capture the Google Ads click identifier (gclid) from the landing URL
    // before any tracking fires, so the gclid cookie is available to the
    // dataLayer-loaded event and to downstream contract stamping (P0-2).
    captureGoogleClickId()

    if (!initializationResult.gtm.initialized) {
      const resultGTM = initializeGTM(config)
      initializationResult.gtm = resultGTM
    } else {
      logTracking('gtm', 'GTM already initialized')
    }

    if (!initializationResult.amp.initialized) {
      const resultAmp = await initializeAmplitude(config, ctx || null, options)
      initializationResult.amp = resultAmp
    } else {
      logTracking('amplitude', 'Amplitude already initialized')
    }

    if (!initializationResult.webExp.initialized) {
      const resultWebExp = initializeWebExperiment(config)
      initializationResult.webExp = resultWebExp
    } else {
      logTracking('web-experiment', 'Web Experiment already initialized')
    }

    return Promise.resolve()
  }

  // returns a function that closes over initialization state
  return async (
    config: AnalyticsConfig,
    ctx?: PageAnalyticsContext,
    options?: InitializeOptions
  ): Promise<InitializationResults> => {
    // Return existing promise if already initializing and another init is called
    // some apps have multiple analytics init calls on page load
    // This will ensure only one initialization happens
    // It's important to initialize amplitude sdk only once
    if (initializationPromise) {
      return initializationPromise
    }

    // Create new initialization promise
    initializationPromise = (async () => {
      try {
        await initializeAllTracking(config, ctx, options)
      } catch (err) {
        initializationResult.msg += 'Error during initialization.'
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        initializationPromise = null

        // Combined error messages
        const additionalMsgs = [
          initializationResult.gtm.msg,
          initializationResult.amp.msg,
          initializationResult.webExp.msg,
        ]
          .filter((msg: string) => msg && msg.trim())
          .join(' ')

        if (additionalMsgs) {
          initializationResult.msg = initializationResult.msg
            ? `${initializationResult.msg} ${additionalMsgs}`
            : additionalMsgs
        }
      }
      return initializationResult
    })()

    return initializationPromise
  }
}

// init has an internal state via the use of closure
// to track whether gtm and amp have been initialized
const init = makeInitFunction()

// Page Load Tracking
function firePageTrackingWithContext(
  gaPath: string = window.location.pathname,
  shouldFireGATracking: boolean = true
): void {
  if (analyticsEnabled.has(analyticTools.ga) && shouldFireGATracking) {
    googleAnalytics.firePageTracking(gaPath)
  }
}

export {
  init,
  createAnalyticsContext,
  identifyUser,
  firePageTrackingWithContext,
  segment,
  standardTracking,
  standardSchemas,
  abTesting,
  captureGoogleClickId,
  readGoogleClickId,
  hashEmail,
  hasAdvertisingConsent,
  ADVERTISING_GROUP,
  setAnalyticsHashedEmail,
  getAnalyticsHashedEmail,
  // eventTracking exports
  defaultSource,
  eventNames,
  track,
  trackDataLayerLoaded,
  trackBuyingPreferencesNudge,
  trackPropertySave,
  trackSearchFilterChanged,
  trackInstrumentPageLoad,
  trackUserDomainEvent,
  // flow tracking exports
  createFlowEmitter,
  composeEmitters,
}

export type {
  EnrichmentPlugin,
  InitializeOptions,
  AmplitudeUserProperties,
} from './amplitude/index'

// Default export for backward compatibility
export default {
  init,
  createAnalyticsContext,
  identifyUser,
  firePageTrackingWithContext,
  segment,
  standardTracking,
  standardSchemas,
  abTesting,
  captureGoogleClickId,
  readGoogleClickId,
  hashEmail,
  hasAdvertisingConsent,
  ADVERTISING_GROUP,
  setAnalyticsHashedEmail,
  getAnalyticsHashedEmail,
  defaultSource,
  eventNames,
  track,
  trackDataLayerLoaded,
  trackBuyingPreferencesNudge,
  trackPropertySave,
  trackSearchFilterChanged,
  trackInstrumentPageLoad,
  trackUserDomainEvent,
  // flow tracking exports
  createFlowEmitter,
  composeEmitters,
}
